/**
 * POI Icon Utility Functions
 *
 * Generates SVG data URIs for POI markers with circular backgrounds and centered icons.
 * Follows HALO pattern from mapMarkers.ts for Google Maps integration.
 */

import { RESTAURANT_ICON_PATH } from './RestaurantIcon';
import { GROCERY_ICON_PATH } from './GroceryIcon';
import { CAFE_ICON_PATH } from './CafeIcon';
import { SHOPPING_ICON_PATH } from './ShoppingIcon';
import { NIGHTLIFE_ICON_PATH } from './NightlifeIcon';

export type POIType = 'restaurants' | 'groceries' | 'cafes' | 'shopping' | 'nightlife';

// Design token to hex color mapping
const DESIGN_TOKEN_COLORS = {
  hpxTeal500: '#007a70',
  hpxRed400: '#c93a1a',
  hpPurple500: '#6D4772', // Actual purple token value from panda.config.ts
  hpxYellow600: '#efae2c', // Yellow background for shopping POI markers
  hpxBlue500: '#2f3c53', // Blue background for nightlife POI markers
} as const;

// Icon path mapping
const POI_ICON_PATHS: Record<POIType, string> = {
  restaurants: RESTAURANT_ICON_PATH,
  groceries: GROCERY_ICON_PATH,
  cafes: CAFE_ICON_PATH,
  shopping: SHOPPING_ICON_PATH,
  nightlife: NIGHTLIFE_ICON_PATH,
};

/**
 * Converts design token to hex color
 */
const getHexColor = (tokenOrHex: string): string => {
  if (tokenOrHex.startsWith('#')) {
    return tokenOrHex;
  }

  // Check if it's a design token
  if (tokenOrHex in DESIGN_TOKEN_COLORS) {
    return DESIGN_TOKEN_COLORS[tokenOrHex as keyof typeof DESIGN_TOKEN_COLORS];
  }

  // Fallback for unknown tokens (shouldn't happen in normal usage)
  console.warn(`Unknown color token: ${tokenOrHex}, using fallback color`);
  return '#007a70'; // hpxTeal500 as fallback
};

/**
 * Generates SVG data URI for POI marker with circular background and centered icon
 *
 * @param poiType - Type of POI (restaurants, groceries, cafes, etc.)
 * @param backgroundColor - Design token or hex color for circle background
 * @param size - Marker size in pixels (default: 14)
 * @returns SVG data URI suitable for Google Maps markers
 */
export const createPOIIconDataURL = (poiType: POIType, backgroundColor: string, size: number = 24): string => {
  const iconPath = POI_ICON_PATHS[poiType];

  if (!iconPath) {
    console.warn(`No icon path defined for POI type: ${poiType}`);
    // Return fallback circle for undefined POI types
    return createFallbackCircleDataURL(backgroundColor, size);
  }

  const bgColor = getHexColor(backgroundColor);
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 1; // Leave 1px for white border

  // For 24x24px circles, use 16x16px icons centered
  // Icons are designed at 16x16px, so scale = 1 for 24px circles
  const iconScale = size >= 24 ? 1 : (size - 4) / 16;
  const iconSize = 16 * iconScale;
  const iconOffset = (size - iconSize) / 2;

  const markerSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <!-- Circular background with white border -->
      <circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="${bgColor}" stroke="white" stroke-width="1"/>

      <!-- Centered icon content -->
      <g transform="translate(${iconOffset}, ${iconOffset}) scale(${iconScale})">
        ${iconPath}
      </g>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`;
};

/**
 * Creates fallback circle data URI for POI types without icons
 */
const createFallbackCircleDataURL = (backgroundColor: string, size: number = 24): string => {
  const bgColor = getHexColor(backgroundColor);
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 1;

  const fallbackSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="${bgColor}" stroke="white" stroke-width="1"/>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(fallbackSvg)}`;
};

/**
 * Type-specific icon data URL generators for convenience
 */
export const getRestaurantIconDataURL = (backgroundColor: string, size?: number): string =>
  createPOIIconDataURL('restaurants', backgroundColor, size);

export const getGroceryIconDataURL = (backgroundColor: string, size?: number): string =>
  createPOIIconDataURL('groceries', backgroundColor, size);

export const getCafeIconDataURL = (backgroundColor: string, size?: number): string =>
  createPOIIconDataURL('cafes', backgroundColor, size);

export const getShoppingIconDataURL = (backgroundColor: string, size?: number): string =>
  createPOIIconDataURL('shopping', backgroundColor, size);

export const getNightlifeIconDataURL = (backgroundColor: string, size?: number): string =>
  createPOIIconDataURL('nightlife', backgroundColor, size);
